import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Instagram, Facebook, Mail, ArrowRight, Gift } from "lucide-react";
import { BUSINESS, IMG, NAV_LINKS } from "../data/content";
import { LOCATIONS } from "../data/locations";
import { scrollToHash } from "../lib/scroll";

const Footer = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goHash = (e, href) => {
    e.preventDefault();
    if (pathname === "/") {
      if (href === "#home") window.scrollTo({ top: 0, behavior: "smooth" });
      else scrollToHash(href);
    } else {
      navigate("/" + (href === "#home" ? "" : href));
    }
  };

  return (
    <footer className="relative bg-primary text-background pt-20 sm:pt-24 overflow-hidden" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-[1.4fr_1fr_1.2fr] gap-12 pb-16">
          {/* Brand + newsletter */}
          <div>
            <img src={IMG.logo} alt="Detour Coffee Co." className="h-11 w-auto brightness-0 invert" />
            <p className="mt-5 max-w-sm text-background/70 leading-relaxed">
              Handcrafted coffee, fresh pastries, and Mediterranean-inspired café favorites in the heart of Arlington, Virginia.
            </p>

            <div className="mt-8 max-w-sm space-y-3" data-testid="footer-signup">
              <p className="text-xs uppercase tracking-label text-highlight font-semibold">Email & SMS offers</p>
              <p className="text-sm text-background/65 leading-relaxed">
                Seasonal drinks, events & exclusive deals — straight to your inbox and phone.
              </p>
              <a
                href={BUSINESS.marketingSignupUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-signup-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-highlight text-foreground px-6 py-3 text-sm font-semibold tracking-wide transition-transform duration-300 hover:scale-[1.03]"
              >
                Sign up for offers <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href={BUSINESS.giftCardUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-giftcard-btn"
                className="flex items-center gap-2 text-sm text-background/75 hover:text-highlight transition-colors"
              >
                <Gift size={15} /> Buy or check a gift card
              </a>
            </div>
          </div>

          {/* Nav */}
          <div>
            <p className="text-xs uppercase tracking-label text-highlight font-semibold mb-5">Explore</p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a href={l.href} onClick={(e) => goHash(e, l.href)} data-testid={`footer-${l.label.toLowerCase()}-link`} className="text-background/75 hover:text-highlight transition-colors">
                    {l.label}
                  </a>
                </li>
              ))}
              <li>
                <Link to="/gift-cards" data-testid="footer-giftcards-link" className="text-background/75 hover:text-highlight transition-colors">
                  Gift Cards
                </Link>
              </li>
            </ul>
            <p className="text-xs uppercase tracking-label text-highlight font-semibold mt-8 mb-4">Neighborhoods</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2" data-testid="footer-locations">
              {LOCATIONS.map((l) => (
                <li key={l.slug}>
                  <Link to={`/coffee-shop/${l.slug}`} data-testid={`footer-location-${l.slug}`} className="text-sm text-background/70 hover:text-highlight transition-colors">
                    {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Hours + contact */}
          <div>
            <p className="text-xs uppercase tracking-label text-highlight font-semibold mb-5">Hours & Contact</p>
            <ul className="space-y-2 text-sm">
              {BUSINESS.hours.map((h) => (
                <li key={h.day} className="flex justify-between gap-4 text-background/75">
                  <span>{h.day}</span>
                  <span>{h.time}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 space-y-1.5 text-sm text-background/75">
              <p>{BUSINESS.address}</p>
              <a href={BUSINESS.phoneHref} className="block hover:text-highlight transition-colors">{BUSINESS.phone}</a>
              <a href={`mailto:${BUSINESS.email}`} className="block hover:text-highlight transition-colors">{BUSINESS.email}</a>
            </div>
            <div className="mt-6 flex gap-3">
              {[
                { icon: Instagram, href: BUSINESS.instagram, label: "Instagram" },
                { icon: Facebook, href: BUSINESS.facebook, label: "Facebook" },
                { icon: Mail, href: `mailto:${BUSINESS.email}`, label: "Email" },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  className="h-11 w-11 rounded-full border border-white/20 flex items-center justify-center transition-colors hover:bg-highlight hover:text-foreground hover:border-highlight"
                >
                  <Icon size={18} strokeWidth={1.6} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Giant brand wordmark */}
      <div className="border-t border-white/10">
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="font-serif text-center leading-none text-[19vw] lg:text-[15vw] text-background/8 select-none pointer-events-none py-4"
        >
          Detour
        </motion.h2>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-background/50">
          <p>© {new Date().getFullYear()} Detour Coffee Co. All rights reserved.</p>
          <p data-testid="footer-credit">
            Website &amp; SEO by{" "}
            <a
              href="https://webnehtech.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-background/75 hover:text-highlight transition-colors"
            >
              WebnehTech
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
